import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Chip,
  IconButton,
} from '@mui/material';
import Button from './Button';
import Loading from './Loading';
import Alert from './Alert';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

const CONTRIBUTOR_LABELS = {
  primary: { label: 'Primary', color: '#34d399', bg: 'rgba(16, 185, 129, 0.12)' },
  grouped: { label: 'Grouped', color: '#60a5fa', bg: 'rgba(59, 130, 246, 0.12)' },
  inherited: { label: 'Inherited', color: '#94a3b8', bg: 'rgba(148, 163, 184, 0.12)' },
  manual: { label: 'Manual', color: '#c084fc', bg: 'rgba(168, 85, 247, 0.12)' },
};

function formatTimestamp(value) {
  if (!value) return null;
  return new Date(value).toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function ContributorFeedbackModal({ isOpen, onClose, reviewedBy, entries = [], loading, error }) {
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: '#1e293b',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          pb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(59, 130, 246, 0.15)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              flexShrink: 0,
            }}
          >
            <PersonIcon sx={{ color: '#60a5fa', fontSize: '1.15rem' }} />
          </Box>
          <Box>
            <Typography sx={{ color: '#e2e8f0', fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.3 }}>
              {reviewedBy || 'Contributor'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              {loading
                ? 'Loading feedback…'
                : `${entries.length} feedback ${entries.length === 1 ? 'entry' : 'entries'}`}
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: '#64748b', '&:hover': { color: '#e2e8f0' } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ background: '#1e293b', pt: 3, maxHeight: '60vh' }}>
        {loading && <Loading message="Loading feedback..." />}
        {!loading && error && <Alert variant="error">{error}</Alert>}
        {!loading && !error && entries.length === 0 && (
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            No feedback entries found for this contributor.
          </Typography>
        )}
        {!loading && !error && entries.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {entries.map((entry, idx) => {
              const style = CONTRIBUTOR_LABELS[entry.contribution_type] || CONTRIBUTOR_LABELS.manual;
              const timestamp = formatTimestamp(entry.reviewed_at);
              return (
                <Box
                  key={`${entry.feedback_id || idx}`}
                  sx={{
                    p: 2,
                    background: '#0f172a',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
                    <Chip
                      label={style.label}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        color: style.color,
                        background: style.bg,
                        border: `1px solid ${style.color}40`,
                      }}
                    />
                    {entry.synthesis_action && (
                      <Chip
                        label={entry.synthesis_action.toUpperCase()}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          color: '#94a3b8',
                          background: 'transparent',
                          border: '1px solid rgba(148, 163, 184, 0.35)',
                        }}
                      />
                    )}
                    {timestamp && (
                      <Typography variant="caption" sx={{ color: '#475569', ml: 'auto', fontSize: '0.72rem' }}>
                        {timestamp}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                    <ChatBubbleOutlineIcon sx={{ color: '#475569', fontSize: '0.95rem', mt: 0.25 }} />
                    <Typography variant="body2" sx={{ color: '#cbd5e1', lineHeight: 1.5, fontSize: '0.85rem' }}>
                      {entry.feedback_reason || 'No reason provided.'}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, background: '#1e293b', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ContributorFeedbackModal;
